import { useCallback, useEffect, useRef } from 'react';
import { useSession } from '../SessionProvider';
import { checkSearchAndRescue } from './checkSearchAndRescue';
import {
  ToastAction,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  useToast,
} from '@ais-ui/ui-core';
import { PiMagnifyingGlassPlus } from 'react-icons/pi';
import {
  isValidCoordinate,
  useOpenLayersContext,
} from '@ais-ui/react-openlayers';
import { fromLonLat } from 'ol/proj';
import { cn } from '@ais-ui/utils';
import { noop } from 'lodash';
import Map from 'ol/Map';

const InvalidToastAction = () => {
  return (
    <ToastAction
      altText="Zoom to man overboard"
      className="flex space-x-2 hover:bg-transparent"
      onClick={noop}
    >
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild disabled={false}>
            <div className={cn('flex space-x-2 opacity-50')}>
              <PiMagnifyingGlassPlus className={'h-4 w-4'} />
              <p>Zoom</p>
            </div>
          </TooltipTrigger>
          <TooltipContent className="opacity-100">
            Cannot zoom: vessel position unknown
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </ToastAction>
  );
};

interface ValidToastActionProps {
  message: { lat: number; lon: number };
  map: Map;
}

const ValidToastAction = ({ message, map }: ValidToastActionProps) => {
  const onZoomToSearchAndRescue = useCallback(
    (latitude: number, longitude: number) => {
      if (!map) {
        return;
      }

      if (!isValidCoordinate(longitude, latitude)) {
        return;
      }

      const view = map.getView();
      const zoomLevel = 15;
      const duration = 0;

      const transformedCoordinates = fromLonLat([longitude, latitude]);

      view.animate({
        center: transformedCoordinates,
        zoom: zoomLevel,
        duration: duration,
      });
    },
    [map]
  );

  return (
    <ToastAction
      altText="Zoom to man overboard"
      className="flex space-x-2 hover:bg-gray-500"
      onClick={() => onZoomToSearchAndRescue(message.lat, message.lon)}
    >
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild disabled={false}>
            <div className={cn('flex space-x-2')}>
              <PiMagnifyingGlassPlus className={'h-4 w-4'} />
              <p>Zoom</p>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            Zoom to vessel with search and rescue responder active
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </ToastAction>
  );
};

export const useCheckSearchAndRescue = (isActive: boolean) => {
  const { olSession } = useSession();
  const { map } = useOpenLayersContext();
  const { toast } = useToast();
  const seenMessages = useRef<Set<string>>(new Set());

  useEffect(() => {
    const fetchSearchAndRescueData = async () => {
      const response = await checkSearchAndRescue(olSession);

      response.messages.forEach((message) => {
        const messageKey = `${message.message}`;
        if (!seenMessages.current.has(messageKey)) {
          seenMessages.current.add(messageKey);
          toast({
            title: 'Man Overboard!',
            description: message.message,
            variant: 'warning',
            duration: 240000, // 4 minutes
            action: isValidCoordinate(message.lon, message.lat) ? (
              <ValidToastAction message={message} map={map} />
            ) : (
              <InvalidToastAction />
            ),
          });
        }
      });
    };

    if (!isActive) {
      return;
    }

    fetchSearchAndRescueData();
    const intervalId = setInterval(fetchSearchAndRescueData, 15000);

    return () => clearInterval(intervalId);
  }, [olSession, isActive, toast]);
};
